import { ArrowPathIcon, BeakerIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  type MockLearnerData,
  type PromptTestStatusOutput,
  type TestFile,
  getPromptTestStatus,
  getTestFileParseStatus,
  listSharedTestFiles,
  listTestFiles,
  startPromptTest,
} from "@api/rest/prompt-sandbox";
import { fetchLearnerTestProfile, listLearnerTestProfiles } from "@api/rest/prompt-test-bed";
import type { OrganizationSummary, SectionField, TemplateDetail } from "@api/rest/types-and-hooks";
import { GeneratingSkeleton } from "@components/LlmOutput/GeneratingSkeleton";
import { LlmOutputView } from "@components/LlmOutput/LlmOutputView";
import { notifyError } from "@components/notifications/notifications";
import { REPORT_TYPE_LABELS } from "@pages/Templates/constants";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ParsedFileEntry extends TestFile {
  parsedJson?: string | null;
  workflowId?: string;
}

type FileEntry = ParsedFileEntry & { source: "template" | "shared" };

interface PromptTestResult {
  fieldId: number;
  promptKey: string;
  fieldTitle: string;
  status: "idle" | "running" | "complete" | "failed";
  result: string | null;
  workflowId?: string;
}

interface Props {
  template: TemplateDetail;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DEFAULT_MOCK_DATA: MockLearnerData = {
  biographic_info: { name: "Test Patient", age: "7", gender: "Male" },
  date_of_report: new Date().toLocaleDateString("en-US"),
  safety_concerns: [],
  short_term_goals: [],
  provider_language: null,
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Prompt Test Result
// ---------------------------------------------------------------------------

const PromptResultView: FC<{ result: PromptTestResult }> = ({ result }) => {
  const isRunning = result.status === "running";
  const isFailed = result.status === "failed";

  if (result.status === "idle") return null;

  return (
    <div className="mt-2 rounded border border-border-subtle bg-bg-default text-xs">
      {isRunning && (
        <div className="p-3">
          <GeneratingSkeleton />
        </div>
      )}
      {isFailed && (
        <div className="p-3">
          <span className="flex items-center gap-1.5 text-text-error-primary">
            <XCircleIcon className="size-3.5" />
            Generation failed. Check that required test files are uploaded and parsed.
          </span>
        </div>
      )}
      {result.status === "complete" && (
        <div className="p-3">
          <LlmOutputView result={result.result} />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const TemplatePromptSandbox: FC<Props> = ({ template }) => {
  const MOCK_KEY = `basecamp-mock-${template.id}`;
  const RESULTS_KEY = `basecamp-results-${template.id}`;

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(template.organizations[0]?.id?.toString() ?? null);

  const [mockData, setMockDataState] = useState<MockLearnerData>(() => {
    try {
      return JSON.parse(localStorage.getItem(MOCK_KEY) ?? "null") ?? DEFAULT_MOCK_DATA;
    } catch {
      return DEFAULT_MOCK_DATA;
    }
  });
  const setMockData = useCallback(
    (v: MockLearnerData) => {
      setMockDataState(v);
      localStorage.setItem(MOCK_KEY, JSON.stringify(v));
    },
    [MOCK_KEY],
  );

  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);

  // Fetch the full profile (not the list stub) so test_files is always populated.
  const { data: selectedProfile = null } = useQuery({
    queryKey: ["learner-test-profile", selectedProfileId],
    queryFn: () => fetchLearnerTestProfile(selectedProfileId!),
    enabled: selectedProfileId !== null,
  });

  // Derived: org of the selected profile for targeted shared-file fetching.
  const profileOrgId = selectedProfile?.organization_id ? String(selectedProfile.organization_id) : null;

  const [parsedJsonMap, setParsedJsonMap] = useState<Map<string, string>>(new Map());
  const [selectedFileUuids, setSelectedFileUuids] = useState<Set<string>>(new Set());

  // When the full profile loads (or changes), sync mock data + file selection.
  useEffect(() => {
    if (!selectedProfile) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMockData(selectedProfile.mock_learner_data ?? DEFAULT_MOCK_DATA);
    setSelectedFileUuids(new Set(selectedProfile.test_files.map((f) => f.file_uuid)));
  }, [selectedProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  const [testResults, setTestResults] = useState<Map<number, PromptTestResult>>(() => {
    try {
      const stored = localStorage.getItem(RESULTS_KEY);
      return stored ? new Map(JSON.parse(stored) as [number, PromptTestResult][]) : new Map();
    } catch {
      return new Map();
    }
  });

  // Persist completed/failed results to localStorage whenever they change
  useEffect(() => {
    const toSave = Array.from(testResults.entries()).filter(
      ([, r]) => r.status === "complete" || r.status === "failed",
    );
    if (toSave.length > 0) localStorage.setItem(RESULTS_KEY, JSON.stringify(toSave));
  }, [testResults, RESULTS_KEY]);

  // Collect all sections fields that have a prompt
  const promptFields: SectionField[] = template.sections.flatMap((s) =>
    s.section_fields.filter((f) => f.prompt !== null),
  );

  // Fetch persisted test files (template-scoped)
  const { data: testFiles = [] } = useQuery({
    queryKey: ["test-files", template.id, selectedOrgId],
    queryFn: () => listTestFiles(template.id, selectedOrgId),
  });

  // Fetch org-shared test files (only when an org is selected)
  const { data: sharedFiles = [] } = useQuery({
    queryKey: ["test-files-shared", selectedOrgId],
    queryFn: () => listSharedTestFiles(selectedOrgId!),
    enabled: !!selectedOrgId,
  });

  // Always fetch the global pool — global profiles store their files here,
  // not in any org-scoped pool.
  const { data: globalSharedFiles = [] } = useQuery({
    queryKey: ["test-files-shared", null],
    queryFn: () => listSharedTestFiles(null),
  });

  // If the selected profile belongs to an org different from the template's
  // selected org, fetch that org's shared pool too so its files appear in allFiles.
  const { data: profileOrgFiles = [] } = useQuery({
    queryKey: ["test-files-shared", profileOrgId],
    queryFn: () => listSharedTestFiles(profileOrgId!),
    enabled: !!profileOrgId && profileOrgId !== selectedOrgId,
  });

  // No report_type filter on purpose — it made report-narrowed global
  // profiles vanish from the picker; the row badge shows the scope instead.
  const { data: profiles = [] } = useQuery({
    queryKey: ["learner-test-profiles", selectedOrgId ? Number(selectedOrgId) : null],
    queryFn: () =>
      listLearnerTestProfiles({
        org_id: selectedOrgId ? Number(selectedOrgId) : null,
      }),
  });

  // Combine template + shared files, deduped by file_uuid — global files
  // can also appear in the org pool.
  const allFiles = useMemo<FileEntry[]>(() => {
    const seen = new Set<string>();
    const entries: FileEntry[] = [];
    for (const f of [
      ...testFiles.map((f) => ({ ...f, source: "template" as const })),
      ...sharedFiles.map((f) => ({ ...f, source: "shared" as const })),
      ...globalSharedFiles.map((f) => ({ ...f, source: "shared" as const })),
      ...profileOrgFiles.map((f) => ({ ...f, source: "shared" as const })),
    ]) {
      if (seen.has(f.file_uuid)) continue;
      seen.add(f.file_uuid);
      entries.push({ ...f, parsedJson: parsedJsonMap.get(f.file_uuid) ?? null, workflowId: f.workflow_id });
    }
    return entries;
  }, [testFiles, sharedFiles, globalSharedFiles, profileOrgFiles, parsedJsonMap]);

  // Hydrate parsedJsonMap for already-complete files; never touches
  // selectedFileUuids, and skips files already in the map.
  useEffect(() => {
    for (const f of testFiles) {
      if (f.parse_status === "complete" && f.workflow_id && !parsedJsonMap.has(f.file_uuid))
        void getTestFileParseStatus(f.workflow_id, {
          template_id: template.id,
          org_id: selectedOrgId,
          file_uuid: f.file_uuid,
        }).then((res) => {
          if (res.parsed_json) setParsedJsonMap((prev) => new Map(prev).set(f.file_uuid, res.parsed_json!));
        });
    }
    for (const f of sharedFiles) {
      if (f.parse_status === "complete" && f.workflow_id && !parsedJsonMap.has(f.file_uuid))
        void getTestFileParseStatus(f.workflow_id, {
          shared: true,
          org_id: selectedOrgId,
          file_uuid: f.file_uuid,
        }).then((res) => {
          if (res.parsed_json) setParsedJsonMap((prev) => new Map(prev).set(f.file_uuid, res.parsed_json!));
        });
    }
    for (const f of globalSharedFiles) {
      if (f.parse_status === "complete" && f.workflow_id && !parsedJsonMap.has(f.file_uuid))
        void getTestFileParseStatus(f.workflow_id, {
          shared: true,
          org_id: null,
          file_uuid: f.file_uuid,
        }).then((res) => {
          if (res.parsed_json) setParsedJsonMap((prev) => new Map(prev).set(f.file_uuid, res.parsed_json!));
        });
    }
    for (const f of profileOrgFiles) {
      if (f.parse_status === "complete" && f.workflow_id && !parsedJsonMap.has(f.file_uuid))
        void getTestFileParseStatus(f.workflow_id, {
          shared: true,
          org_id: profileOrgId,
          file_uuid: f.file_uuid,
        }).then((res) => {
          if (res.parsed_json) setParsedJsonMap((prev) => new Map(prev).set(f.file_uuid, res.parsed_json!));
        });
    }
  }, [testFiles, sharedFiles, globalSharedFiles, profileOrgFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildTestFilesPayload = useCallback((): { file_kind: string; parsed_json: string; filename: string }[] => {
    return allFiles
      .filter((f) => f.parse_status === "complete" && f.parsedJson && selectedFileUuids.has(f.file_uuid))
      .map((f) => ({ file_kind: f.file_kind, parsed_json: f.parsedJson!, filename: f.original_filename }));
  }, [allFiles, selectedFileUuids]);

  // Poll result for a running prompt test
  const pollPromptResult = useCallback((fieldId: number, workflowId: string) => {
    const iv = setInterval(async () => {
      try {
        const res: PromptTestStatusOutput = await getPromptTestStatus(workflowId);
        if (res.status === "complete") {
          clearInterval(iv);
          setTestResults((prev) =>
            new Map(prev).set(fieldId, {
              ...prev.get(fieldId)!,
              status: "complete",
              result: res.result,
            }),
          );
        } else if (res.status === "failed") {
          clearInterval(iv);
          setTestResults((prev) =>
            new Map(prev).set(fieldId, {
              ...prev.get(fieldId)!,
              status: "failed",
              result: null,
            }),
          );
        }
      } catch {
        /* ignore */
      }
    }, 2000);
  }, []);

  const handleGenerate = useCallback(
    async (field: SectionField) => {
      if (!field.prompt) return;
      const testFilesPayload = buildTestFilesPayload();

      setTestResults((prev) =>
        new Map(prev).set(field.id, {
          fieldId: field.id,
          promptKey: field.prompt!.key,
          fieldTitle: field.title,
          status: "running",
          result: null,
        }),
      );

      try {
        const { workflow_id } = await startPromptTest({
          prompt_key: field.prompt.key,
          test_files: testFilesPayload,
          mock_learner_data: mockData,
        });
        setTestResults((prev) =>
          new Map(prev).set(field.id, {
            ...prev.get(field.id)!,
            workflowId: workflow_id,
          }),
        );
        pollPromptResult(field.id, workflow_id);
      } catch {
        notifyError("Failed to start prompt test.");
        setTestResults((prev) =>
          new Map(prev).set(field.id, {
            ...prev.get(field.id)!,
            status: "failed",
          }),
        );
      }
    },
    [buildTestFilesPayload, mockData, pollPromptResult],
  );

  const parsedFileCount = allFiles.filter(
    (f) => f.parse_status === "complete" && selectedFileUuids.has(f.file_uuid),
  ).length;

  // file_kinds of the selected parsed files — used to skip prompts whose
  // input_dependency has no backing file, matching the portal's behavior.
  const availableFileKinds = useMemo(
    () =>
      new Set(
        allFiles
          .filter((f) => f.parse_status === "complete" && selectedFileUuids.has(f.file_uuid))
          .map((f) => f.file_kind),
      ),
    [allFiles, selectedFileUuids],
  );

  // Eligible = no data-source dependency, or at least one dependency has a
  // matching parsed file. Section deps are inherited by fields without their own.
  const eligibleFieldIds = useMemo(() => {
    const ids = new Set<number>();
    for (const section of template.sections) {
      const sectionDeps = (section.input_dependency as string[] | null) ?? [];
      for (const field of section.section_fields) {
        if (!field.prompt) continue;
        const fieldDeps = (field.input_dependency as string[] | null) ?? [];
        const deps = fieldDeps.length ? fieldDeps : sectionDeps;
        if (deps.length === 0 || deps.some((d) => availableFileKinds.has(d))) {
          ids.add(field.id);
        }
      }
    }
    return ids;
  }, [template.sections, availableFileKinds]);

  const missingDepsForField = useCallback(
    (field: SectionField, section: { input_dependency: unknown | null }): string[] => {
      const fieldDeps = (field.input_dependency as string[] | null) ?? [];
      const sectionDeps = (section.input_dependency as string[] | null) ?? [];
      const deps = fieldDeps.length ? fieldDeps : sectionDeps;
      if (deps.length === 0) return [];
      return deps.filter((d) => !availableFileKinds.has(d));
    },
    [availableFileKinds],
  );

  const handleGenerateAll = useCallback(async () => {
    const fields = promptFields.filter((f) => f.prompt && eligibleFieldIds.has(f.id));
    await Promise.all(fields.map((f) => handleGenerate(f)));
  }, [promptFields, handleGenerate, eligibleFieldIds]);

  return (
    <div className="flex gap-6 min-h-0">
      {/* Left: Test Files + Mock Data */}
      <div className="w-72 shrink-0 flex flex-col gap-4">
        {/* Org selector */}
        {template.organizations.length > 1 && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-secondary">Organization scope</span>
            <select
              value={selectedOrgId ?? ""}
              onChange={(e) => setSelectedOrgId(e.target.value || null)}
              className="text-sm border border-border-subtle rounded px-2 py-1.5 bg-bg-default focus:outline-none focus:ring-1 focus:ring-limestone-900"
            >
              {template.organizations.map((org: OrganizationSummary) => (
                <option key={org.id} value={String(org.id)}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Profile picker */}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-text-primary">Learner Profile</span>
          <select
            value={selectedProfileId?.toString() ?? ""}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              setSelectedProfileId(id);
              if (!id) setSelectedFileUuids(new Set());
            }}
            className="w-full text-sm border border-border-subtle rounded px-2 py-1.5 bg-bg-default focus:outline-none focus:ring-1 focus:ring-limestone-900"
          >
            <option value="">Select a profile…</option>
            {profiles.map((p) => {
              const scopeParts: string[] = [];
              if (!p.organization_id) scopeParts.push("global org");
              if (p.report_types.length > 0) {
                scopeParts.push(
                  p.report_types
                    .map((rt) => REPORT_TYPE_LABELS[rt as keyof typeof REPORT_TYPE_LABELS] ?? rt)
                    .join(" / "),
                );
              } else {
                scopeParts.push("any report type");
              }
              return (
                <option key={p.id} value={p.id}>
                  {p.name} — {scopeParts.join(" · ")}
                </option>
              );
            })}
          </select>
          <div className="flex items-center gap-1.5">
            <Link
              to="/evaluations/profiles/new"
              className="text-xs px-2 py-1 rounded border border-border-subtle bg-bg-default hover:bg-bg-hover"
            >
              + New profile
            </Link>
            <Link
              to="/evaluations/profiles"
              className="text-xs px-2 py-1 rounded border border-border-subtle bg-bg-default hover:bg-bg-hover"
            >
              Manage profiles
            </Link>
          </div>

          {selectedProfile ? (
            <div className="rounded-md border border-border-subtle bg-bg-default p-3 flex flex-col gap-2">
              {selectedProfile.mock_learner_data?.biographic_info && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-text-secondary">Learner</span>
                  <span className="text-xs text-text-primary">
                    {[
                      selectedProfile.mock_learner_data.biographic_info.name,
                      selectedProfile.mock_learner_data.biographic_info.age,
                      selectedProfile.mock_learner_data.date_of_report,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
              )}
              {selectedProfile.test_files.length > 0 ? (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-text-secondary">
                    Files ({selectedProfile.test_files.length})
                  </span>
                  <ul className="flex flex-col gap-0.5">
                    {selectedProfile.test_files.map((f) => (
                      <li key={f.file_uuid} className="text-xs text-text-primary truncate">
                        {f.filename}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <span className="text-xs text-text-tertiary italic">No files attached to this profile.</span>
              )}
            </div>
          ) : profiles.length === 0 ? (
            <span className="text-xs text-text-tertiary italic">
              No profiles yet — click &ldquo;+ New&rdquo; to create one.
            </span>
          ) : null}
        </div>

        {parsedFileCount === 0 && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            {!selectedProfile
              ? "Select a learner profile to generate prompt output."
              : selectedProfile.test_files.length === 0
                ? "The selected profile has no files. Add files on the Profiles page."
                : "Profile files are not yet parsed. Generation will be available once parsing completes."}
          </p>
        )}
      </div>

      {/* Right: Prompt fields */}
      <div className="flex-1 min-w-0 flex flex-col gap-4 overflow-auto">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BeakerIcon className="size-5 text-text-tertiary" />
            <span className="text-sm font-semibold text-text-primary">AI Fields ({promptFields.length})</span>
          </div>
          {promptFields.length > 0 && (
            <button
              type="button"
              disabled={
                parsedFileCount === 0 ||
                eligibleFieldIds.size === 0 ||
                promptFields.some((f) => testResults.get(f.id)?.status === "running")
              }
              onClick={() => void handleGenerateAll()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-border-medium text-text-secondary hover:bg-bg-default disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title={
                eligibleFieldIds.size < promptFields.length
                  ? `${promptFields.length - eligibleFieldIds.size} field${promptFields.length - eligibleFieldIds.size === 1 ? " is" : "s are"} skipped because the uploaded files don't match their data sources.`
                  : undefined
              }
            >
              <BeakerIcon className="size-3.5" />
              Generate All ({eligibleFieldIds.size}
              {eligibleFieldIds.size < promptFields.length ? ` of ${promptFields.length}` : ""})
            </button>
          )}
        </div>

        {promptFields.length === 0 ? (
          <p className="text-sm text-text-tertiary">
            No fields with prompts assigned. Add prompts to fields in the Structure tab.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {template.sections.map((section) => {
              const sectionPromptFields = section.section_fields.filter((f) => f.prompt !== null);
              if (sectionPromptFields.length === 0) return null;
              return (
                <div key={section.id}>
                  <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">
                    {section.title}
                  </p>
                  <div className="flex flex-col gap-2">
                    {sectionPromptFields.map((field) => {
                      const result = testResults.get(field.id);
                      const isRunning = result?.status === "running";
                      const missing = missingDepsForField(field, section);
                      const eligible = eligibleFieldIds.has(field.id);
                      return (
                        <div key={field.id} className="rounded-md border border-border-subtle bg-bg-surface p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex flex-col gap-0.5 min-w-0">
                              <span className="text-sm font-medium text-text-primary truncate">{field.title}</span>
                              <span className="text-xs text-text-tertiary">
                                <span className="font-medium text-text-primary">{field.prompt!.name}</span>{" "}
                                <code className="bg-bg-default px-1 rounded text-xs">{field.prompt!.key}</code>
                              </span>
                              {!eligible && missing.length > 0 && (
                                <span className="text-xs text-amber-700">
                                  Skipped in Generate All — no uploaded file for: {missing.join(", ")}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              disabled={isRunning || parsedFileCount === 0}
                              onClick={() => handleGenerate(field)}
                              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-border-medium text-text-secondary hover:bg-bg-default disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              title={
                                !eligible && missing.length > 0
                                  ? `Data sources not present in uploaded files: ${missing.join(", ")}`
                                  : undefined
                              }
                            >
                              {isRunning ? (
                                <>
                                  <ArrowPathIcon className="size-3.5 animate-spin" />
                                  Running…
                                </>
                              ) : (
                                <>
                                  <BeakerIcon className="size-3.5" />
                                  Generate
                                </>
                              )}
                            </button>
                          </div>
                          {result && <PromptResultView result={result} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
